/**
 * <gg-promotions> — cart-drawer promotions widget.
 *
 * Thin renderer over gg-promotions-shared.js. On every cart change it reads the
 * fresh cart, finds active promotions that apply, and renders a progress bar +
 * claim/picker flow below the checkout button. The native Shopify discount is the
 * money truth — we never set a price; we only add the gift line (tagged _gg_gift)
 * when the customer clicks to claim, and only when the threshold is met + in stock.
 *
 * We do NOT auto-add: "auto" gift mode is treated as auto-ELIGIBLE (shows a claim
 * button). This unifies fixed + picker gifts and drops the auto-add/reject/remove
 * race machinery — we keep only: serialized mutations, fresh /cart.js reads,
 * double-add guard, quantity-lock, fail-safe.
 */

import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import {
  fetchActivePromotions,
  fetchCart,
  matchingPromotions,
  showsOn,
  lineSubject,
  progressToward,
  reachedTier,
  mediaHTML,
  giftDetail,
  giftItem,
  sizedImg,
  availableGiftVariants,
  isConsumed,
  showsBar,
  localeText,
  titleFor,
  barHTML,
  giftActionHTML,
  animateProgressFills,
  esc,
  gidToId,
  peekClaimIntent,
  clearClaimIntent,
  applyPromoCode,
  rememberRemovedCode,
  PROMO_OPEN_CART,
} from '@theme/promotions-shared';

class GgPromotions extends HTMLElement {
  #lang = 'de';
  #mutating = Promise.resolve(); // primitive 1: serialize all cart mutations
  #openPickerId = null; // promo id whose picker list is expanded (survives re-renders)
  #promos = [];
  #cart = { items: [] };
  #dialog = null;
  #promoCodes = new Set(); // upper-cased codes of active code-based promos (for removal memory)

  connectedCallback() {
    this.#lang = this.dataset.lang || 'de';
    if (this.dataset.giftIcon) window.__ggGiftIcon = this.dataset.giftIcon;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Opening the drawer morphs in the server HTML (this element empty) without firing
    // cart:update — re-render when the enclosing <dialog> opens so content isn't blank.
    this.#dialog = this.closest('dialog');
    this.#dialog?.addEventListener('toggle', this.#onCartUpdate);
    // A PDP/banner claim signals here (covers the already-open drawer, where showDialog
    // fires no toggle). Re-render, then #runPendingIntent consumes the stashed intent.
    document.addEventListener(PROMO_OPEN_CART, this.#onCartUpdate);
    // When the shopper removes a discount pill (theme's native × on .cart-discount__pill),
    // remember it if it's one of OUR promo codes so auto-apply won't re-add it this session.
    // Capture phase: the pill's own handler stops propagation, so we must see it first.
    document.addEventListener('click', this.#onPillRemove, true);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#dialog?.removeEventListener('toggle', this.#onCartUpdate);
    document.removeEventListener(PROMO_OPEN_CART, this.#onCartUpdate);
    document.removeEventListener('click', this.#onPillRemove, true);
  }

  #onCartUpdate = () => {
    this.#render();
  };

  // Record a removed promo code so #render's auto-apply respects the shopper's × click.
  // Only OUR codes are remembered — a shopper's own code (WILLKOMMEN10) is left alone.
  #onPillRemove = (e) => {
    const btn = e.target instanceof Element ? e.target.closest('.cart-discount__pill-remove') : null;
    if (!btn) return;
    const code = btn.closest('.cart-discount__pill')?.dataset.discountCode;
    if (code && this.#promoCodes.has(String(code).toUpperCase())) rememberRemovedCode(code);
  };

  async #render() {
    const [promotions, cart] = await Promise.all([
      fetchActivePromotions(),
      fetchCart(), // primitive 2: always read /cart.js fresh (coalesced across surfaces)
    ]);

    // Every non-gift cart line becomes a matching subject; a promo shows if it
    // matches ANY line (i.e. something in the cart could work toward it).
    const subjects = (cart.items || [])
      .filter((l) => !l.properties?._gg_gift)
      .map(lineSubject);
    // Consumed promos drop out — a claimed gift (in cart with its Geschenk tag) or a tiered
    // promo whose top tier is reached (nothing left to progress toward). Shared so all agree.
    const relevant = promotions.filter(
      (p) =>
        !isConsumed(cart, p) &&
        showsOn(p, ['cart_drawer', 'cart_page']) && // honor Flow surface toggles (drawer == cart page here)
        subjects.some((s) => matchingPromotions([p], s).length)
    );

    // Keep the last render's data for click handlers (no shared-object mutation).
    this.#promos = relevant;
    this.#cart = cart;

    // Auto-apply code-based promos (backend `code` field). A promo that carries a real
    // Shopify code AND currently qualifies gets its code applied via the Cart API →
    // Shopify returns the truly discounted cart (real strikethrough + discount row), no
    // widget price math. applyPromoCode is apply-once + honors session removals, so this
    // runs safely on every cart:update. Serialized through #mutating so it never races
    // a gift add. `code:null` (automatic) promos are skipped inside applyPromoCode.
    //
    // Applying is a MONEY concern, not a display one, so it runs off the scope-matched
    // active promos — NOT `relevant` (which drops `isConsumed` promos). A tiered promo
    // whose top tier the cart jumped straight past is `isConsumed` (no progress UI left)
    // but its code must still apply. We only need scope-match + qualification here.
    const codeCandidates = promotions.filter(
      (p) => p.code && subjects.some((s) => matchingPromotions([p], s).length)
    );
    this.#promoCodes = new Set(codeCandidates.map((p) => String(p.code).toUpperCase()));
    this.#applyCodes(codeCandidates, cart);

    if (!relevant.length) {
      this.innerHTML = '';
      return;
    }
    const rows = await Promise.all(relevant.map((p) => this.#renderPromo(p, cart)));
    this.innerHTML = rows.join('');
    this.#wire();
    animateProgressFills(this);
    this.#runPendingIntent();
    // Re-open a picker that was expanded before this render (state, not DOM, is the truth —
    // so a re-render triggered by cart:update/drawer-toggle doesn't collapse the list).
    if (this.#openPickerId != null) await this.#renderPicker(this.#openPickerId);
  }

  /**
   * Act on a claim intent handed over from the PDP/banner (via requestClaimInCart):
   * a met FIXED gift → add it silently (with toast, no drawer needed); a met PICKER → mark
   * its list open so #render shows it. The intent is cleared only after we act, so a render
   * where the promo isn't listed yet leaves it for the next (no consume-and-lose).
   */
  #runPendingIntent() {
    const intent = peekClaimIntent();
    if (!intent) return;
    const promo = this.#promoById(intent.promoId);
    if (!promo) return; // not rendered yet → leave the intent for a later render
    clearClaimIntent();
    if (intent.mode === 'pick') {
      this.#openPickerId = String(promo.id); // #render (below/next) draws the list
    } else {
      this.#claim(promo, availableGiftVariants(promo)[0]);
    }
  }

  async #renderPromo(promo, cart) {
    const progress = progressToward(cart, promo);
    const text = localeText(promo, this.#lang) || {};
    const [media, detail] = await Promise.all([mediaHTML(promo, cart), giftDetail(promo)]);

    const titleRow = `<div class="promo-title"><span class="promo-dot"></span>${titleFor(promo, text)}</div>`;
    const isPicker = promo.reward?.gift?.mode === 'picker';
    // Picker keeps its detail line hidden (the bar/Choose carry it); fixed shows title+detail.
    const detailLine = detail && !(progress.met && isPicker) ? `<div class="promo-detail">${esc(detail)}</div>` : '';
    const action = giftActionHTML(promo, progress); // met claim button / Choose / vergriffen, else ''
    const bar = showsBar(promo, progress) ? barHTML(promo, progress, text, cart) : '';

    return this.#row(promo, media, `${titleRow}${detailLine}${action}${bar}`);
  }

  #row(promo, media, body) {
    return `<div class="gg-promo__row" data-promo-id="${promo.id}">
      <div class="promo-media">${media}</div>
      <div class="gg-promo__body">${body}</div>
    </div>`;
  }

  #wire() {
    // stopPropagation on every promo click so it never bubbles to the cart drawer's
    // light-dismiss handler (which would close the drawer when you interact with us).
    this.querySelectorAll('[data-promo-claim]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const promo = this.#promoById(btn.dataset.promoClaim);
        if (promo) this.#claim(promo, availableGiftVariants(promo)[0]);
      })
    );
    this.querySelectorAll('[data-promo-pick]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = String(btn.dataset.promoPick);
        // Toggle open/closed as widget state, then reflect it in the DOM.
        this.#openPickerId = this.#openPickerId === id ? null : id;
        if (this.#openPickerId) this.#renderPicker(id);
        else this.querySelector('.gg-promo__picker')?.remove();
      })
    );
  }

  /** Build + append the picker list for `promoId` below its row (idempotent — replaces any). */
  async #renderPicker(promoId) {
    const promo = this.#promoById(promoId);
    const row = this.querySelector(`.gg-promo__row[data-promo-id="${promoId}"]`);
    if (!promo || !row) return;
    // Drop any stale list first so re-renders don't stack duplicates.
    this.querySelectorAll('.gg-promo__picker').forEach((el) => el.remove());
    const variants = availableGiftVariants(promo);
    const list = document.createElement('div');
    list.className = 'gg-promo__picker';
    list.innerHTML = (await Promise.all(variants.map((vid) => this.#giftRow(promo, vid)))).join('');
    row.after(list); // below the header row (keeps title + bar visible), per Figma
    list.querySelectorAll('[data-pick-variant]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#openPickerId = null; // picking closes the list
        this.#claim(promo, b.dataset.pickVariant);
      })
    );
  }

  async #giftRow(promo, variantGid) {
    // Payload-first (reward.gift.items) via the shared resolver; /variants/{id}.js fallback.
    const info = await giftItem(promo, variantGid);
    const media = info?.image
      ? `<img class="promo-media__img" src="${sizedImg(info.image)}" alt="" aria-hidden="true">`
      : (window.__ggGiftIcon ? `<img class="promo-media__icon" src="${window.__ggGiftIcon}" alt="" aria-hidden="true">` : '');
    // Title on line 1, brand on line 2 (stacked, per Figma) — not comma-joined.
    return `<div class="gg-promo__row">
      <div class="promo-media">${media}</div>
      <div class="gg-promo__body">
        <div class="promo-detail promo-detail--title">${esc(info?.title || 'Geschenk')}</div>
        ${info?.vendor ? `<div class="promo-detail">${esc(info.vendor)}</div>` : ''}
        <button type="button" class="promo-action" data-pick-variant="${variantGid}">Auswählen</button>
      </div>
    </div>`;
  }

  /**
   * Apply the Shopify discount code of every qualifying code-based promo (auto-apply).
   * A promo qualifies when its threshold is met (gift/GWP promos) or a tier is reached
   * (tiered promos). Serialized through #mutating so it never races a gift add. On a real
   * apply, morph the cart section + re-render from the discounted cart (the strikethrough
   * now lives in the server HTML). applyPromoCode no-ops for automatic/already-applied/
   * shopper-removed codes, so calling this on every render is safe.
   */
  #applyCodes(promos, cart) {
    const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
    const qualifying = promos.filter((p) => p.code && this.#codeQualifies(p, cart));
    if (!qualifying.length) return;

    for (const promo of qualifying) {
      this.#mutating = this.#mutating
        .then(async () => {
          // Re-read fresh: an earlier code in this chain may have changed the cart's codes.
          const fresh = await fetchCart();
          const data = await applyPromoCode(promo.code, fresh, sectionId);
          if (!data) return; // no-op (already applied / removed / rejected)
          if (sectionId && data.sections?.[sectionId]) morphSection(sectionId, data.sections[sectionId]);
          // Re-render on the next frame so whichever <gg-promotions> is mounted after the
          // morph reflects the discounted cart (same pattern as #addGift).
          requestAnimationFrame(() => document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate)));
        })
        .catch(() => {});
    }
  }

  /** Does a code-based promo currently qualify for its discount? Tiered → a tier reached;
   *  everything else → its threshold is met. Mirrors the render/consume predicates. */
  #codeQualifies(promo, cart) {
    if (promo.type === 'tiered') return reachedTier(cart, promo) != null;
    return progressToward(cart, promo).met;
  }

  /** Claim a gift: add the chosen variant with the _gg_gift property (serialized). */
  #claim(promo, variantId) {
    if (!variantId) return;
    this.#mutating = this.#mutating.then(() => this.#addGift(promo, variantId)).catch(() => {});
    return this.#mutating;
  }

  async #addGift(promo, variantId) {
    // primitive 3: double-add guard — re-read fresh, bail if already present.
    const cart = await fetch('/cart.js').then((r) => r.json());
    if ((cart.items || []).some((l) => l.properties?._gg_gift === String(promo.id))) return;

    const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
    const body = {
      items: [
        {
          id: gidToId(variantId),
          quantity: promo.reward?.gift?.quantity || 1, // primitive 5: qty locked to the reward
          properties: { _gg_gift: String(promo.id) },
        },
      ],
    };
    if (sectionId) body.sections = [sectionId];

    const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(body) }));
    if (!res.ok) {
      // Stock changed between cache and add (V2-A race) → show vergriffen, never an error.
      this.#render();
      return;
    }
    const data = await res.json();

    // Enrich the cart toast with the gift's photo + brand (payload-first, like the rows).
    // Falls back to the gift icon when there's no product image, so the toast is never blank.
    const info = await giftItem(promo, variantId);
    document.dispatchEvent(
      new CustomEvent('gg:cart-toast', {
        detail: {
          title: info?.title || 'Geschenk hinzugefügt',
          brand: info?.vendor || '',
          thumb: info?.image ? sizedImg(info.image) : window.__ggGiftIcon || '',
        },
      })
    );

    if (sectionId && data.sections?.[sectionId]) {
      morphSection(sectionId, data.sections[sectionId]);
    }
    // Always re-render the widget from fresh cart, AFTER any morph settles. The morph
    // replaces the cart DOM (including this element) with the server's EMPTY widget, and
    // its own cart:update can race our in-flight render → the other promos vanish until
    // reload. Dispatching on the next frame guarantees whichever <gg-promotions> is now
    // mounted re-renders against the cart that includes the just-added gift (claimed promo
    // drops, the rest stay). Idempotent: a plain event, the standard render path.
    requestAnimationFrame(() =>
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate))
    );
  }

  #promoById(id) {
    return this.#promos.find((p) => String(p.id) === String(id));
  }
}

if (!customElements.get('gg-promotions')) {
  customElements.define('gg-promotions', GgPromotions);
}
